import { useState } from 'react';
import { formatMoney, parseAmount } from '@domain';
import type { Contact } from '@domain';
import type { DataSource, DeliveryDetail, RegistrationInput } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { Card, PageTitle, Pill } from '../components/ui.js';
import { DeliveryCosts } from './DeliveryCosts.js';
import { DeliveryResultCard } from './DeliveryResultCard.js';
import { DeliveryCredits } from './DeliveryCredits.js';

/**
 * One delivery: what it is, who is on it, and what that comes to.
 *
 * The numbers across the top are the centre's own — seats, expected revenue,
 * how full. None of them is an accounting figure and none comes from Xero;
 * what the delivery actually earned and cost is a separate question, waiting
 * on how a delivery gets stamped onto a Xero transaction.
 *
 * Saying "expected" rather than "revenue" is the whole point of that
 * distinction. One is a booking sheet, the other is a ledger.
 */

const STATUS_PILL: Record<string, string> = {
  registered: 'DRAFT',
  attended: 'PAID',
  cancelled: 'VOIDED',
  refunded: 'VOIDED',
};

const PHASE_PILL: Record<string, { status: string; label: string }> = {
  upcoming: { status: 'DRAFT', label: 'Upcoming' },
  running: { status: 'READY', label: 'Running now' },
  finished: { status: 'PAID', label: 'Finished' },
  cancelled: { status: 'VOIDED', label: 'Cancelled' },
};

function blank(deliveryId: number): RegistrationInput {
  return {
    registrationId: null,
    deliveryId,
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    billContactId: null,
    fee: null,
    discount: 0,
    status: 'registered',
  };
}

/** Xero's own words for where an invoice stands, as pill colours. */
const INVOICE_PILL: Record<string, string> = {
  DRAFT: 'DRAFT',
  'AWAITING APPROVAL': 'REVIEW',
  'AWAITING PAYMENT': 'SENT',
  'PART PAID': 'LOW',
  OVERDUE: 'OVERDUE',
  PAID: 'PAID',
  VOIDED: 'VOIDED',
};

export function DeliveryScreen({
  detail,
  contacts,
  loading,
  busy,
  error,
  canClose,
  source,
  t,
  onSave,
  onInvoice,
  onOpenInvoice,
  onLinkCost,
  onUnlinkCost,
  onNewBillForDelivery,
  onLinkCredit,
  onUnlinkCredit,
  onSetClosed,
  onBack,
}: {
  detail: DeliveryDetail | null;
  /** Xero's customers, so a student can be billed to one that exists. */
  contacts: Contact[];
  loading: boolean;
  busy: boolean;
  error: string | null;
  /** Closing and reopening are an administrator's call. See functions/trainingCosts.ts. */
  canClose: boolean;
  source: DataSource;
  t: Translate;
  onSave: (registration: RegistrationInput) => void;
  onInvoice: (
    registrationIds: number[] | null,
    date: string,
    /** Blank means send nothing, so the company's Xero terms apply. */
    dueDate: string | null,
  ) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onLinkCost: (cost: {
    source: 'bill' | 'spend';
    xeroId: string;
    allocatedAmount: number | null;
    note: string | null;
  }) => void;
  onUnlinkCost: (costId: number) => void;
  /** File a new bill already attached to this cohort. */
  onNewBillForDelivery: () => void;
  onLinkCredit: (credit: { xeroId: string; allocatedAmount: number | null; note: string | null }) => void;
  onUnlinkCredit: (creditId: number) => void;
  onSetClosed: (closed: boolean) => void;
  onBack: () => void;
}) {
  const [editing, setEditing] = useState<RegistrationInput | null>(null);
  /*
    The date the invoices will carry, which decides the period the revenue
    lands in. Null until somebody changes it, so the server's own suggestion
    is used — and so a cohort whose dates are edited while this screen is
    open does not keep a stale date that was never typed.
  */
  const [invoiceDate, setInvoiceDate] = useState<string | null>(null);
  /*
    When the invoices fall due.

    Empty is the ordinary answer and a deliberate one: the company's own
    Xero settings carry its payment terms, and sending nothing lets them
    apply. Typing a date here overrides them for this run only — which is
    what a corporate client on their own terms needs.
  */
  const [dueDate, setDueDate] = useState('');

  const field = {
    height: '34px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  const cell = {
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  if (loading || detail === null) {
    return (
      <div style={{ padding: '22px 24px 40px', maxWidth: '1040px' }}>
        <Card>
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {error ? t(error) : t('Loading…')}
          </div>
        </Card>
      </div>
    );
  }

  const {
    delivery,
    registrations,
    numbers,
    currency,
    invoices,
    cannotInvoice,
    costs,
    credits,
    result,
    perSeat,
    suggestedInvoiceDate,
  } = detail;

  /** What the date box shows: the person's choice, or the server's. */
  const datedFor = invoiceDate ?? suggestedInvoiceDate;

  /*
    Closed means closed: no bookings, no invoices, no costs.

    Hiding the buttons is courtesy; the API refuses every one of these
    independently, which is what actually makes the result worth quoting.
  */
  const locked = delivery.closedAt !== null;

  const contactOptions: SelectOption[] = contacts.map((contact) => ({
    value: contact.id,
    label: contact.name,
  }));

  /** What is still to be billed, so the button can say how many. */
  const unbilled = registrations.filter(
    (registration) =>
      (registration.status === 'registered' || registration.status === 'attended') &&
      registration.xeroInvoiceId === null,
  ).length;

  /** The single invoice a corporate engagement is billed on. */
  const companyInvoice = delivery.xeroInvoiceId
    ? (invoices[delivery.xeroInvoiceId] ?? null)
    : null;
  const phase = PHASE_PILL[delivery.phase] ?? PHASE_PILL.upcoming!;
  const corporate = delivery.kind === 'corporate';
  const ready = editing !== null && editing.studentName.trim() !== '' && !busy;

  const stat = (title: string, value: string, hint?: string) => (
    <div key={title}>
      <div style={{ fontSize: '11.5px', color: theme.textMuted, fontWeight: 600 }}>{title}</div>
      <div dir="ltr" style={{ fontFamily: theme.fontMono, fontSize: '19px', fontWeight: 600 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: '11px', color: theme.textFaint }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1040px' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'transparent',
          border: 0,
          color: theme.textMuted,
          fontSize: '12.5px',
          fontWeight: 600,
          cursor: 'pointer',
          padding: 0,
          marginBottom: '8px',
        }}
      >
        ← {t('All deliveries')}
      </button>

      <PageTitle
        title={delivery.name}
        sub={`${delivery.courseName}${delivery.trainer ? ` · ${delivery.trainer}` : ''}${
          delivery.venue ? ` · ${delivery.venue}` : ''
        }`}
      />

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '12px 0 16px' }}>
        <Pill status={phase.status} label={t(phase.label)} />
        <span style={{ fontSize: '12.5px', color: theme.textMuted, fontFamily: theme.fontMono }} dir="ltr">
          {delivery.startsOn}
          {delivery.endsOn && delivery.endsOn !== delivery.startsOn ? ` → ${delivery.endsOn}` : ''}
        </span>
        <span style={{ fontSize: '12.5px', color: theme.textMuted }}>
          {corporate ? t('Corporate booking') : t('Public course')}
        </span>
      </div>

      {error && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(error)}
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: '14px' }}>
        <div
          style={{
            padding: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '30px',
            alignItems: 'baseline',
          }}
        >
          {stat(
            t('On the course'),
            delivery.capacity === null
              ? String(numbers.live)
              : `${numbers.live} / ${delivery.capacity}`,
            numbers.percentFull === null ? t('no seat limit') : `${numbers.percentFull}% ${t('full')}`,
          )}

          {numbers.cancelled > 0 &&
            stat(t('Cancelled'), String(numbers.cancelled), t('not counted in the total'))}

          {stat(
            t('Expected revenue'),
            `${formatMoney(numbers.expectedRevenue)} ${currency}`,
            corporate ? t('the agreed contract fee') : t('from live bookings'),
          )}

          {/*
            A corporate booking has one invoice, not a count of billed
            seats. "0 / 2 seats billed" would be true of a company that has
            been invoiced in full, which is the wrong thing to tell somebody
            chasing payment.
          */}
          {!corporate &&
            stat(
              t('Invoiced in Xero'),
              `${numbers.invoicedSeats} / ${numbers.live}`,
              t('seats billed so far'),
            )}
        </div>

        {corporate && (
          <div
            style={{
              padding: '13px 16px',
              borderTop: `1px solid ${theme.dividerSoft}`,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '10px',
              fontSize: '12.5px',
            }}
          >
            <span style={{ color: theme.textMuted, fontWeight: 600 }}>
              {t("The company's invoice")}
            </span>
            {companyInvoice ? (
              <>
                <Pill
                  status={INVOICE_PILL[companyInvoice.status] ?? 'DRAFT'}
                  label={t(companyInvoice.status)}
                />
                {/* An invoice number is one token and never breaks. */}
                <span
                  dir="ltr"
                  style={{
                    fontFamily: theme.fontMono,
                    color: theme.textMuted,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {companyInvoice.number}
                </span>
                {companyInvoice.amountDue > 0 && (
                  <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                    {formatMoney(companyInvoice.amountDue)} {currency} {t('still owed')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onOpenInvoice(companyInvoice.invoiceId)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.border}`,
                    borderRadius: theme.radiusControl,
                    padding: '5px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('View')}
                </button>
              </>
            ) : (
              <span style={{ color: theme.textMuted }}>{t('Not raised yet')}</span>
            )}
          </div>
        )}

        {numbers.oversold && (
          <div
            style={{
              padding: '11px 16px',
              borderTop: `1px solid ${theme.dividerSoft}`,
              color: theme.warnDeep,
              fontWeight: 600,
              fontSize: '12.5px',
            }}
          >
            {t('More bookings than seats.')}{' '}
            <span style={{ fontWeight: 400 }}>
              {t('Nothing is blocked — this is here so it is not a surprise on the day.')}
            </span>
          </div>
        )}

        {/*
          Said plainly, because the card below says something that looks
          similar and is not. These are bookings — what people agreed to
          pay. What the course actually earned is further down, and comes
          from invoices in Xero rather than from this list.
        */}
        <div
          style={{
            padding: '11px 16px',
            borderTop: `1px solid ${theme.dividerSoft}`,
            fontSize: '11.5px',
            color: theme.textMuted,
          }}
        >
          {t('These are bookings — what people agreed to pay. What the course actually earned is below.')}
        </div>
      </Card>

      {/* No currency prop: the card uses the one the figures are actually
          in, which is not the company's when a cohort was billed abroad. */}
      <DeliveryResultCard
        result={result}
        perSeat={perSeat}
        closedAt={delivery.closedAt}
        t={t}
      />

      {locked && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '13px 16px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px',
              fontSize: '12.5px',
            }}
          >
            <Pill status="PAID" label={t('Closed')} />
            <span style={{ color: theme.textMuted, flex: '1 1 240px', lineHeight: 1.5 }}>
              {t('Nothing more can be added. The figures above are final unless it is reopened.')}
            </span>
            {canClose && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onSetClosed(false)}
                style={{
                  height: '34px',
                  padding: '0 15px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  fontSize: '12.5px',
                  fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {t('Reopen this course')}
              </button>
            )}
          </div>
        </Card>
      )}

      {/*
        Billing the cohort.

        One press for a public cohort bills every student who has not been
        billed yet; for a corporate booking it raises the single invoice to
        the company. Which of those happens is decided on the server, from
        what was sold — see api/src/domain/trainingInvoice.ts.
      */}
      {!locked && (
      <Card style={{ marginBottom: '14px' }}>
        <div
          style={{
            padding: '14px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            disabled={busy || cannotInvoice !== null}
            onClick={() => onInvoice(null, datedFor, dueDate === '' ? null : dueDate)}
            style={{
              height: '38px',
              padding: '0 18px',
              background: cannotInvoice === null && !busy ? theme.nav : theme.borderStrong,
              color: '#fff',
              border: 0,
              borderRadius: theme.radiusControl,
              fontSize: '13px',
              fontWeight: 600,
              cursor: cannotInvoice === null && !busy ? 'pointer' : 'default',
            }}
          >
            {/* Counts only while there is something to count. "Invoice 0
                students" on a disabled button is a silly way to say the
                cohort is done, and the line beside it already says it. */}
            {busy
              ? t('Creating invoices…')
              : corporate
                ? t('Invoice the company')
                : unbilled === 0
                  ? t('Invoice the cohort')
                  : `${t('Invoice')} ${unbilled} ${unbilled === 1 ? t('student') : t('students')}`}
          </button>

          {/*
            The date the invoices carry.

            Not a detail: it decides which month's profit and loss this
            course's revenue appears in. Offered as the day the course
            finished rather than today, so a September cohort invoiced in
            November is still September's revenue — and editable, because
            the period it would land in may already be locked in Xero.
          */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {t('Invoice date')}
            </span>
            <input
              type="date"
              value={datedFor}
              dir="ltr"
              onChange={(e) => setInvoiceDate(e.target.value)}
              style={{
                height: '34px',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '0 10px',
                fontSize: '13px',
                background: '#fff',
                fontFamily: theme.fontMono,
              }}
            />
          </label>

          {/*
            Due date, left blank on purpose.

            The company's payment terms live in its own Xero settings, and
            an invoice sent without a due date takes them. Filling this in
            would override a setting the client configured and go stale
            the day they change it — so it stays empty unless somebody
            means to override it for this run.
          */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {t('Due')}
            </span>
            <input
              type="date"
              value={dueDate}
              dir="ltr"
              onChange={(e) => setDueDate(e.target.value)}
              style={{
                height: '34px',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '0 10px',
                fontSize: '13px',
                background: '#fff',
                fontFamily: theme.fontMono,
              }}
            />
            <span style={{ fontSize: '11px', color: theme.textMuted, whiteSpace: 'nowrap' }}>
              {dueDate === '' ? t("blank = the company's own terms") : t('just this run')}
            </span>
          </label>

          <span
            style={{
              fontSize: '11.5px',
              color: cannotInvoice === null ? theme.textMuted : theme.warnDeep,
              flex: '1 1 240px',
              lineHeight: 1.5,
              fontWeight: cannotInvoice === null ? 400 : 600,
            }}
          >
            {cannotInvoice === 'already_invoiced'
              ? t('Everything here has been invoiced.')
              : cannotInvoice === 'nobody_to_invoice'
                ? t('Add somebody to the course first.')
                : cannotInvoice === 'no_fees_to_bill'
                  ? t('Nobody here has a fee to bill.')
                  : cannotInvoice === 'corporate_needs_a_company'
                    ? t('Choose the company this corporate booking is for.')
                    : cannotInvoice === 'corporate_needs_a_fee'
                      ? t('Enter the agreed contract fee for this corporate booking.')
                      : corporate
                        ? t('One invoice to the company for the agreed fee. It enters the ledger at once.')
                        : t('Each student becomes a customer in Xero. The invoices enter the ledger at once.')}
          </span>
        </div>
      </Card>

      )}

      <DeliveryCosts
        deliveryId={delivery.deliveryId}
        costs={costs}
        currency={currency}
        locked={locked}
        busy={busy}
        source={source}
        t={t}
        onLink={onLinkCost}
        onUnlink={onUnlinkCost}
        onNewBill={onNewBillForDelivery}
      />

      <DeliveryCredits
        deliveryId={delivery.deliveryId}
        credits={credits}
        currency={currency}
        locked={locked}
        busy={busy}
        source={source}
        t={t}
        onLink={onLinkCredit}
        onUnlink={onUnlinkCredit}
      />

      {!locked && canClose && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '13px 16px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px',
              fontSize: '12.5px',
            }}
          >
            <button
              type="button"
              disabled={busy}
              onClick={() => onSetClosed(true)}
              style={{
                height: '36px',
                padding: '0 16px',
                background: 'transparent',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                fontSize: '13px',
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {t('Close this course')}
            </button>
            {/*
              Says what closing does and, as importantly, what it does not.

              It stops this screen being added to. It does not lock Xero,
              and claiming otherwise would be a promise the product cannot
              keep — Xero is the accounting system and stays editable there.
              The figures are re-read from Xero every time this page opens,
              so a closed course still shows the truth if somebody edits an
              invoice afterwards.
            */}
            <span style={{ color: theme.textMuted, flex: '1 1 260px', lineHeight: 1.5 }}>
              {t('Do this when every invoice is raised and every bill is in. Nothing more can be added here afterwards.')}{' '}
              {t('Xero itself stays open: an invoice or bill changed there will still change the figures above, because they are read from Xero every time.')}
            </span>
          </div>
        </Card>
      )}

      {editing === null && !locked && (
        <button
          type="button"
          onClick={() => setEditing(blank(delivery.deliveryId))}
          style={{
            background: 'transparent',
            color: theme.text,
            border: `1px dashed ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '14px',
          }}
        >
          + {corporate ? t('Add an attendee') : t('Add a student')}
        </button>
      )}

      {editing !== null && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {editing.registrationId === null
              ? corporate
                ? t('Add an attendee')
                : t('Add a student')
              : t('Edit booking')}
          </div>

          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <label style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span style={label}>{t('Name')}</span>
                <input
                  value={editing.studentName}
                  onChange={(e) => setEditing({ ...editing, studentName: e.target.value })}
                  style={field}
                  autoFocus
                />
              </label>

              <label style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span style={label}>
                  {t('Email')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
                </span>
                <input
                  value={editing.studentEmail ?? ''}
                  dir="ltr"
                  onChange={(e) => setEditing({ ...editing, studentEmail: e.target.value })}
                  style={field}
                />
              </label>

              <label style={{ flex: '0 1 170px', minWidth: 0 }}>
                <span style={label}>
                  {t('Phone')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
                </span>
                <input
                  value={editing.studentPhone ?? ''}
                  dir="ltr"
                  onChange={(e) => setEditing({ ...editing, studentPhone: e.target.value })}
                  style={field}
                />
              </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              {/* A corporate attendee has no per-head price — the company
                  agreed one fee for the engagement — so the boxes that would
                  only ever be left empty are not shown. */}
              {!corporate && (
                <>
                  <label style={{ flex: '0 1 170px', minWidth: 0 }}>
                    <span style={label}>
                      {t('Fee')}{' '}
                      <span style={{ fontWeight: 400, color: theme.textMuted }}>
                        ({t('blank = usual')})
                      </span>
                    </span>
                    <input
                      value={editing.fee === null ? '' : String(editing.fee)}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          fee: e.target.value.trim() === '' ? null : parseAmount(e.target.value),
                        })
                      }
                      inputMode="decimal"
                      dir="ltr"
                      style={{ ...field, fontFamily: theme.fontMono }}
                      placeholder={
                        delivery.defaultFee === null ? '' : String(delivery.defaultFee)
                      }
                    />
                  </label>

                  <label style={{ flex: '0 1 150px', minWidth: 0 }}>
                    <span style={label}>{t('Discount')}</span>
                    <input
                      value={editing.discount === 0 ? '' : String(editing.discount)}
                      onChange={(e) =>
                        setEditing({ ...editing, discount: parseAmount(e.target.value) })
                      }
                      inputMode="decimal"
                      dir="ltr"
                      style={{ ...field, fontFamily: theme.fontMono }}
                    />
                  </label>
                </>
              )}

              <label style={{ flex: '0 1 170px', minWidth: 0 }}>
                <span style={label}>{t('Status')}</span>
                <select
                  value={editing.status}
                  onChange={(e) =>
                    setEditing({ ...editing, status: e.target.value as RegistrationInput['status'] })
                  }
                  style={field}
                >
                  <option value="registered">{t('Registered')}</option>
                  <option value="attended">{t('Attended')}</option>
                  <option value="cancelled">{t('Cancelled')}</option>
                  <option value="refunded">{t('Refunded')}</option>
                </select>
              </label>
            </div>

            {/*
              Who the invoice goes to.

              Optional on purpose. Left alone, invoicing creates a Xero
              customer from the student's own name — with the email and
              phone typed above — and keeps it, so the second invoice
              reuses the same one. Set it only when the bill goes
              somewhere else: a parent, an employer, a sponsor.
            */}
            <label style={{ display: 'block', maxWidth: '420px', marginBottom: '16px' }}>
              <span style={label}>
                {t('Bill to')}{' '}
                <span style={{ fontWeight: 400, color: theme.textMuted }}>
                  ({t('leave blank to bill the student')})
                </span>
              </span>
              <SearchableSelect
                value={editing.billContactId ?? ''}
                options={contactOptions}
                placeholder={corporate ? t('The company') : t('The student themselves')}
                emptyLabel={t('No contacts in Xero for this organisation yet')}
                onChange={(value) =>
                  setEditing({ ...editing, billContactId: value === '' ? null : value })
                }
                t={t}
              />
            </label>

            <div style={{ display: 'flex', gap: '9px' }}>
              <button
                type="button"
                disabled={!ready}
                onClick={() => {
                  onSave(editing);
                  setEditing(null);
                }}
                style={{
                  height: '38px',
                  minWidth: '150px',
                  background: ready ? theme.nav : theme.borderStrong,
                  color: '#fff',
                  border: 0,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: ready ? 'pointer' : 'default',
                }}
              >
                {busy ? t('Saving…') : t('Save')}
              </button>

              <button
                type="button"
                onClick={() => setEditing(null)}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        {registrations.length === 0 ? (
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Nobody is booked on this yet.')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={heading}>{t('Name')}</th>
                <th style={heading}>{t('Contact')}</th>
                {!corporate && <th style={{ ...heading, textAlign: 'end' }}>{t('Fee')}</th>}
                <th style={heading}>{t('Status')}</th>
                <th style={heading}>{t('Invoice')}</th>
                <th style={{ ...heading, textAlign: 'end' }}>{t('Still owed')}</th>
                <th style={{ ...heading, width: '150px' }} />
              </tr>
            </thead>
            <tbody>
              {registrations.map((registration) => {
                const net = Math.max(
                  0,
                  (registration.fee ?? delivery.defaultFee ?? 0) - registration.discount,
                );
                const live = registration.status === 'registered' || registration.status === 'attended';
                const invoice = registration.xeroInvoiceId
                  ? (invoices[registration.xeroInvoiceId] ?? null)
                  : null;
                return (
                  // A cancelled booking is kept on the record but faded, so
                  // its fee does not read as money the course is expecting.
                  <tr key={registration.registrationId} style={{ opacity: live ? 1 : 0.5 }}>
                    <td style={{ ...cell, fontWeight: 600 }}>{registration.studentName}</td>
                    <td style={{ ...cell, color: theme.textMuted, fontSize: '12px' }} dir="ltr">
                      {registration.studentEmail || registration.studentPhone || '—'}
                    </td>
                    {!corporate && (
                      <td
                        style={{
                          ...cell,
                          textAlign: 'end',
                          fontFamily: theme.fontMono,
                          direction: 'ltr',
                        }}
                      >
                        {formatMoney(net)}
                        {registration.discount > 0 && (
                          <span style={{ color: theme.textMuted, fontSize: '11px' }}>
                            {` −${formatMoney(registration.discount)}`}
                          </span>
                        )}
                      </td>
                    )}
                    <td style={cell}>
                      <Pill
                        status={STATUS_PILL[registration.status] ?? 'DRAFT'}
                        label={t(
                          registration.status.charAt(0).toUpperCase() + registration.status.slice(1),
                        )}
                      />
                    </td>
                    {/*
                      The invoice as Xero holds it now, not merely whether
                      one was made. "Invoiced: yes" tells a training centre
                      nothing; whether the student has paid is the question.
                    */}
                    <td style={cell}>
                      {invoice ? (
                        <button
                          type="button"
                          onClick={() => onOpenInvoice(invoice.invoiceId)}
                          title={t('Open this invoice')}
                          style={{
                            background: 'transparent',
                            border: 0,
                            padding: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            /*
                              Wrap between the pill and the number, never
                              inside either. A narrow column was breaking
                              "INV-9101" across two lines, which reads as
                              two different things.
                            */
                            flexWrap: 'wrap',
                            textAlign: 'start',
                          }}
                        >
                          <Pill
                            status={INVOICE_PILL[invoice.status] ?? 'DRAFT'}
                            label={t(invoice.status)}
                          />
                          <span
                            dir="ltr"
                            style={{
                              fontFamily: theme.fontMono,
                              fontSize: '11.5px',
                              color: theme.textMuted,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {invoice.number}
                          </span>
                        </button>
                      ) : (
                        <span style={{ fontSize: '12px', color: theme.textMuted }}>
                          {t('Not yet')}
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        ...cell,
                        textAlign: 'end',
                        fontFamily: theme.fontMono,
                        direction: 'ltr',
                        color: invoice && invoice.amountDue > 0 ? theme.text : theme.textMuted,
                      }}
                    >
                      {invoice ? (invoice.amountDue > 0 ? formatMoney(invoice.amountDue) : '—') : ''}
                    </td>
                    <td style={{ ...cell, textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {invoice ? (
                        <button
                          type="button"
                          onClick={() => onOpenInvoice(invoice.invoiceId)}
                          style={{
                            background: 'transparent',
                            border: `1px solid ${theme.border}`,
                            borderRadius: theme.radiusControl,
                            padding: '5px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            marginInlineEnd: '6px',
                          }}
                        >
                          {t('View')}
                        </button>
                      ) : (
                        /*
                          No per-attendee invoice on a corporate booking. The
                          company is the customer and there is one invoice
                          for the agreed fee — a button on somebody's row
                          would raise it, which is not what that row says.
                        */
                        live &&
                        !corporate && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              onInvoice(
                                [registration.registrationId],
                                datedFor,
                                dueDate === '' ? null : dueDate,
                              )
                            }
                            style={{
                              background: 'transparent',
                              border: `1px solid ${theme.border}`,
                              borderRadius: theme.radiusControl,
                              padding: '5px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: busy ? 'default' : 'pointer',
                              marginInlineEnd: '6px',
                            }}
                          >
                            {t('Invoice')}
                          </button>
                        )
                      )}
                      {/* The API refuses a change to a closed cohort, so
                          offering the button would only produce an error. */}
                      {!locked && (
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({
                              registrationId: registration.registrationId,
                              deliveryId: delivery.deliveryId,
                              studentName: registration.studentName,
                              studentEmail: registration.studentEmail ?? '',
                              studentPhone: registration.studentPhone ?? '',
                              billContactId: registration.billContactId,
                              fee: registration.fee,
                              discount: registration.discount,
                              status: registration.status,
                            })
                          }
                          style={{
                            background: 'transparent',
                            border: `1px solid ${theme.border}`,
                            borderRadius: theme.radiusControl,
                            padding: '5px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {t('Edit')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
